import { authorize, authorizeAeid } from './Authorization'
import { getClientSettings } from './ClientSettings'
import CookieSessionManager from './CookieSessionManager'
import { sendTrackEvent } from './Events'
import GloboIdClientMap from './GloboIdClientMap'
import Log from './Log'
import { deletePreferences, getPreferences, updatePreferences } from './Preferences'
import RedirectManager from './RedirectManager'
import { TokenSessionManager } from './TokenSessionManager'
import { checkBrowserIsWebview } from './Utils'

const COOKIE_SESSION_MANAGEMENT = 'cookie'
const TOKEN_SESSION_MANAGEMENT = 'token'

class GloboId {
  constructor() {
    this.isInitialized = false

    this.settings = {}
    this.defaultConfiguration = {
      'url': 'https://id.qa.globoi.com/auth',
      'realm': 'globo.com',
      'grantType': 'code',
      'promiseType': 'native',
      'checkLoginIframe': false,
      'redirectUri': window.location.href.replace(/#.*$/, ''),
      'sessionManagement': TOKEN_SESSION_MANAGEMENT,
      'authorizationUrl': '',
      'authorizationAeidUrl': '',
      'enable-pkce': true,
      'pkceMethod': 'S256',
      'logoutUrl': '',
      'legacyCompatible': false,
      'sdkVersion': this.getVersion(),
      'provisioningBaseUrl': 'https://login.globo.com/provisionamento',
      'enableRefreshTokenMode': false,
    }

    this.requiredSettingsList = [
      'clientId',
      'resource',
    ]
  }

  async setup(settings) {
    if (this.isInitialized) {
      Log.debug('Already set up. Doing nothing')
      return
    }

    if (!(settings && Object.keys(settings).length)) {
      throw new Error('The settings given are invalid')
    }

    //retrocompatibility 1.3.1 - 1.4.0
    const legacyUrlConf = settings.authServerUrl || settings.oidcProvider
    if (legacyUrlConf) {
      const url = new URL(legacyUrlConf)
      settings.url = `${url.protocol}//${url.host}/auth`
      delete settings.authServerUrl
      delete settings.oidcProvider
    }

    this.requiredSettingsList.forEach((key) => {
      if (!settings[key]) {
        throw new Error(`Object settings is missing key "${key}"!`)
      }
    })

    let remoteSettings = {}

    try {
      remoteSettings = await getClientSettings(settings.clientId)
      console.debug('Remote settings fetched', remoteSettings)
    } catch (error) {
      console.debug('Error fetching remote settings', error)
    }

    // eslint-disable-next-line no-undef
    settings.logoutUrl = `${AUTHX_FE_URL}`

    this.settings = {
      ...this.defaultConfiguration,
      ...settings,
      ...remoteSettings
    }

    let SessionManagerClass

    switch (this.settings.sessionManagement) {
    case COOKIE_SESSION_MANAGEMENT:
      SessionManagerClass = CookieSessionManager
      break

    case TOKEN_SESSION_MANAGEMENT:
      SessionManagerClass = TokenSessionManager
      break

    default:
      throw new Error(`Invalid session manager type: ${this.settings.sessionManagement}`)
    }

    this.isInitialized = true
    Log.debug(`Settings: ${JSON.stringify(this.settings)}`)
    this.sessionManager = new SessionManagerClass(this.settings)
    await this.setupSilentIfEnabled()

    return this.sessionManager.setup()
  }

  _checkIsInitialized() {
    if (this.isInitialized) {
      return
    }

    throw new Error('The lib is not initilized')
  }

  isLogged() {
    this._checkIsInitialized()
    return this.sessionManager.isLogged()
  }

  async setupSilentIfEnabled() {
    const ssoEvent = {
      clientId: this.settings.clientId,
      sdkVersion: this.getVersion(),
      eventType: `SDK.WEB.${this.sessionManager.oidcIssuer}.SSO`,
      isWebView: checkBrowserIsWebview(),
      sessionManagement: this.settings.sessionManagement,
      legacyCompatible: this.settings.legacyCompatible,
    }

    if (this.settings.onLoad == 'check-sso') {
      if (this.isLogged()) {
        ssoEvent.eventType = `${ssoEvent.eventType}.SUCCEEDED`
        ssoEvent.glbId = this.sessionManager.getCookieGLBID()
        ssoEvent.globoId = this.sessionManager.getUserGloboId()
        ssoEvent.fsId = this.settings.sessionManagement === 'token' && this.sessionManager.getTokenData().fs_id

        sendTrackEvent(ssoEvent)

        console.debug('Globoidjs - Avoiding silenth auth. client already logged')
        this.settings.onLoad = ''
      } else {
        console.debug('Globoidjs - Setting up silent auth configs')
        this.settings.silentCheckSsoRedirectUri = this.settings.silentCheckSsoRedirectUri || this.settings.redirectUri
      }
    } else {
      ssoEvent.eventType = `${ssoEvent.eventType}.NOT.REQUESTED`
      sendTrackEvent(ssoEvent)
    }
  }

  async login() {
    this._checkIsInitialized()

    await this.sessionManager.login()
    console.debug('Running login')
  }

  async loginCustomSettings(options) {
    Log.debug('Initiating custom login: ', options)
    this._checkIsInitialized()

    await this.sessionManager.loginCustomSettings({ ...this.settings, ...options })
    console.debug('Running login')
  }

  async logout() {
    this._checkIsInitialized()

    await this.sessionManager.logout()
    Log.debug('Running logout')
  }

  async authorize(serviceIdList) {
    this._checkIsInitialized()

    console.debug('Running authorize')
    const tokens = await this.sessionManager.RefreshTokens(true)
    const accessToken = tokens['access_token']
    // eslint-disable-next-line no-undef
    const authorizationUrl = this.settings.authorizationUrl || `${DEVICE_AUTHORIZE_URL}`

    return await authorize(accessToken, serviceIdList, authorizationUrl)
  }

  async authorizeAeid(type) {
    this._checkIsInitialized()

    console.debug('Running authorize aeid')
    const tokens = await this.sessionManager.RefreshTokens(true)
    const accessToken = tokens['access_token']
    // eslint-disable-next-line no-undef
    const authorizationAeidUrl = this.settings.authorizationAeidUrl || `${DEVICE_AUTHORIZE_URL_AEID_TYPE}`

    return await authorizeAeid(accessToken, type, authorizationAeidUrl)
  }

  async getUserPreferences(globoid, scope, keys, page, perPage, apiUrl) {
    this._checkIsInitialized()

    console.debug('Fetching user preferences')
    const tokens = await this.sessionManager.RefreshTokens()
    const accessToken = tokens['access_token']

    return await getPreferences(accessToken, apiUrl, globoid, scope, keys, page, perPage)
  }

  async updateUserPreferences(scope, attributes) {
    this._checkIsInitialized()

    console.debug('Updating user preferences')
    const tokens = await this.sessionManager.RefreshTokens()
    const accessToken = tokens['access_token']

    return await updatePreferences(accessToken, scope, attributes)
  }

  async deleteUserPreference(scope, key) {
    this._checkIsInitialized()

    console.debug('Deleting user preference')
    const tokens = await this.sessionManager.RefreshTokens()
    const accessToken = tokens['access_token']

    return await deletePreferences(accessToken, scope, key)
  }

  async loadUserInfo() {
    this._checkIsInitialized()
    Log.debug('Running getUserInfo')
    return await this.sessionManager.loadUserInfo()
  }

  getVersion() {
    // eslint-disable-next-line no-undef
    return SDK_VERSION
  }

  async getExtraInfo() {
    console.debug('Getting extra info from clientId:', this.settings.clientId)
    const rm = new RedirectManager()
    return rm.getExtraInfo(this.settings.clientId)
  }

  async getTokens() {
    this._checkIsInitialized()
    return this.sessionManager.getTokens()
  }

  async redirectToProvisioning(serviceId, callbackUrl) {
    this._checkIsInitialized()

    if (!serviceId) {
      throw new Error('Parameter "serviceId" is required for provisioning redirection.')
    }

    if (!callbackUrl) {
      throw new Error('Parameter "callbackUrl" is required for provisioning redirection.')
    }

    if (!this.settings.provisioningBaseUrl) {
      throw new Error('Provisioning base URL is not configured.')
    }

    const provisioningUrl = `${this.settings.provisioningBaseUrl}/${serviceId}?url=${encodeURIComponent(callbackUrl)}`

    window.location.href = provisioningUrl
  }

  async getPreviewUser() {
    return await this.sessionManager.getPreviewUser()
  }

  async getUpdatedTokens() {
    this._checkIsInitialized()
    return await this.sessionManager.getUpdatedTokens()
  }
}

const initClientMap = async (debug = false) => {
  Log.setDebug(debug)
  Log.debug('Running initQueue')

  window.glb = window.glb || {}
  window.glb.globoIdClientMap = window.glb.globoIdClientMap || new GloboIdClientMap()
}

export {
  COOKIE_SESSION_MANAGEMENT, GloboId,
  initClientMap, TOKEN_SESSION_MANAGEMENT
}

